import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { Info, Filter, Lock } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;
const REGION_NAMES = {
  canada: "Canada", usa: "USA", jamaica: "Jamaica",
  barbados: "Barbados", trinidad_tobago: "Trinidad & Tobago", panama: "Panama"
};

function ScatterPlot({ points, onClickPoint }) {
  const size = 500;
  const pad = 50;
  const inner = size - pad * 2;
  const scale = (v) => pad + ((v - 1) / 4) * inner;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[600px] mx-auto" data-testid="scatter-plot">
      {/* Grid */}
      {[1, 2, 3, 4, 5].map((v) => (
        <g key={v}>
          <line x1={scale(v)} y1={pad} x2={scale(v)} y2={size - pad} stroke="var(--border-default)" strokeWidth="0.5" />
          <line y1={scale(v)} x1={pad} y2={scale(v)} x2={size - pad} stroke="var(--border-default)" strokeWidth="0.5" />
          <text x={scale(v)} y={size - pad + 18} textAnchor="middle" fontSize="10" fill="var(--text-muted)">{v}</text>
          <text x={pad - 12} y={size - scale(v) + pad + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">{v}</text>
        </g>
      ))}

      {/* Diagonal */}
      <line x1={scale(1)} y1={size - scale(1)} x2={scale(5)} y2={size - scale(5)} stroke="var(--border-emphasis)" strokeWidth="1" strokeDasharray="4,4" />

      {/* Zone labels */}
      <text x={scale(1.5)} y={size - scale(4)} fontSize="10" fill="var(--color-info)" fontWeight="500" opacity="0.7">Hidden Talent</text>
      <text x={scale(3.8)} y={size - scale(1.3)} fontSize="10" fill="var(--color-danger)" fontWeight="500" opacity="0.7">Coaching Need</text>

      {/* Points */}
      {points.map((p, i) => {
        const cx = scale(p.sa_score);
        const cy = size - scale(p.mv_score);
        const color = p.category === "under_rater" ? "var(--color-info)" : p.category === "over_rater" ? "var(--color-danger)" : "var(--text-muted)";
        return (
          <g key={i} onClick={() => onClickPoint(p)} className="cursor-pointer">
            <circle cx={cx} cy={cy} r={5} fill={color} opacity={0.7} className="transition-all hover:opacity-100" style={{ transition: "r 150ms" }}>
              <title>{`${p.name}\nSA: ${p.sa_score} | MV: ${p.mv_score} | Gap: ${p.gap > 0 ? "+" : ""}${p.gap}`}</title>
            </circle>
          </g>
        );
      })}

      {/* Axes */}
      <text x={size / 2} y={size - 8} textAnchor="middle" fontSize="11" fill="var(--text-secondary)" fontWeight="500">Self-Assessment Score</text>
      <text x={12} y={size / 2} textAnchor="middle" fontSize="11" fill="var(--text-secondary)" fontWeight="500" transform={`rotate(-90, 12, ${size / 2})`}>Manager Validation Score</text>
    </svg>
  );
}

export default function DemoWorkforceHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("");
  const [dept, setDept] = useState("");
  const [category, setCategory] = useState("");
  const [clickedDot, setClickedDot] = useState(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const params = new URLSearchParams();
    if (region) params.set("region", region);
    if (dept) params.set("department", dept);
    if (category) params.set("category", category);
    const cacheKey = "dash_heatmap_" + params.toString();
    if (!data) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) { setData(JSON.parse(cached)); setLoading(false); }
      } catch {}
    }
    setLoading(prev => !data ? prev : true);
    axios.get(`${API}/heatmap?${params.toString()}`).then(r => {
      setData(r.data);
      try { localStorage.setItem(cacheKey, JSON.stringify(r.data)); } catch {}
    }).catch(console.error).finally(() => setLoading(false));
  }, [region, dept, category]);

  if (loading && !data) {
    return <DemoLayout><div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm" style={{ color: "var(--text-muted)" }}>Loading Heatmap...</div></div></DemoLayout>;
  }

  const summary = data?.summary || {};

  return (
    <DemoLayout>
      <div className="space-y-5" data-testid="workforce-heatmap">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Workforce Heatmap</h1>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>SA vs MV alignment scatter plot &mdash; {summary.total || 0} employees plotted</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap items-center">
          <Filter className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <select value={region} onChange={e => setRegion(e.target.value)} className="px-3 py-1.5 rounded-md text-[12px]"
            style={{ background: "var(--surface-card)", border: "var(--border-width) solid var(--border-default)", color: "var(--text-primary)" }}>
            <option value="">All Regions</option>
            {Object.entries(REGION_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={category} onChange={e => setCategory(e.target.value)} className="px-3 py-1.5 rounded-md text-[12px]"
            style={{ background: "var(--surface-card)", border: "var(--border-width) solid var(--border-default)", color: "var(--text-primary)" }}>
            <option value="">All Categories</option>
            <option value="under_rater">Under-raters (Blue)</option>
            <option value="aligned">Aligned (Gray)</option>
            <option value="over_rater">Over-raters (Red)</option>
          </select>
          <select value={dept} onChange={e => setDept(e.target.value)} className="px-3 py-1.5 rounded-md text-[12px] max-w-[200px]"
            style={{ background: "var(--surface-card)", border: "var(--border-width) solid var(--border-default)", color: "var(--text-primary)" }}>
            <option value="">All Departments</option>
            {(data?.filters?.departments || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Under-raters", value: summary.under_raters || 0, color: "var(--color-info)", desc: "Humble performers" },
            { label: "Aligned", value: summary.aligned || 0, color: "var(--text-muted)", desc: "Accurate self-awareness" },
            { label: "Over-raters", value: summary.over_raters || 0, color: "var(--color-danger)", desc: "Coaching needed" },
            { label: "Total Plotted", value: summary.total || 0, color: "var(--text-primary)", desc: "Both SA + MV done" },
          ].map(c => (
            <div key={c.label} className="card-m p-3 text-center">
              <div className="metric-val text-xl" style={{ color: c.color }}>{c.value}</div>
              <div className="text-[10px] font-medium" style={{ color: "var(--text-secondary)" }}>{c.label}</div>
              <div className="text-[9px]" style={{ color: "var(--text-muted)" }}>{c.desc}</div>
            </div>
          ))}
        </div>

        {/* Scatter Plot */}
        <div className="card-m p-5 relative">
          {/* Blur overlay on dot click */}
          {clickedDot && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center rounded-xl cursor-pointer"
              style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", background: isDark ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.6)" }}
              onClick={() => setClickedDot(null)}
              data-testid="heatmap-overlay"
            >
              <div className="text-center p-6 rounded-xl" style={{ background: isDark ? "rgba(30,30,30,0.95)" : "rgba(255,255,255,0.95)", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, maxWidth: 320 }}>
                <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ background: clickedDot.category === "under_rater" ? "#3b82f620" : clickedDot.category === "over_rater" ? "#ef444420" : "#71767b20" }}>
                  <Lock className="w-5 h-5" style={{ color: clickedDot.category === "under_rater" ? "#3b82f6" : clickedDot.category === "over_rater" ? "#ef4444" : "#71767b" }} />
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
                  {clickedDot.category === "under_rater" ? "Hidden Talent (Under-rater)" : clickedDot.category === "over_rater" ? "Coaching Needed (Over-rater)" : "Well-Calibrated"}
                </p>
                <p className="text-xs mb-1" style={{ color: "var(--text-secondary)" }}>
                  SA: {clickedDot.sa_score?.toFixed(1)} · MV: {clickedDot.mv_score?.toFixed(1)} · Gap: {clickedDot.gap > 0 ? "+" : ""}{clickedDot.gap?.toFixed(1)}
                </p>
                <p className="text-[11px] mt-2 px-3 py-1.5 rounded-lg" style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", color: isDark ? "#9ca3af" : "#6b7280" }}>
                  Available after assessment completion
                </p>
                <p className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>Click anywhere to dismiss</p>
              </div>
            </div>
          )}
          {(data?.points?.length || 0) > 0 ? (
            <ScatterPlot points={data.points} onClickPoint={p => setClickedDot(p)} />
          ) : (
            <div className="py-16 text-center text-[13px]" style={{ color: "var(--text-muted)" }}>
              No data points match the current filters. Need both SA and MV completed for an employee to appear.
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="card-m p-4 text-[12px]" style={{ color: "var(--text-secondary)" }}>
          <strong>How to read:</strong> Each dot is one employee. Dots <strong style={{ color: "var(--color-info)" }}>ABOVE</strong> the diagonal = manager rates them higher than they rate themselves (humble / under-rater). Dots <strong style={{ color: "var(--color-danger)" }}>BELOW</strong> the diagonal = they rate themselves higher than their manager does (overconfident). Click any dot to see full profile.
        </div>
      </div>
    </DemoLayout>
  );
}
